import React, { useState } from 'react';
import { Box, Button, Chip, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { useDialog } from '@applications-terrains/birdz-react-library';
import { useTranslation } from 'react-i18next';
import ConfirmDialog, { ConfirmDialogOptions } from '../../ConfirmDialog';
import { useInterventionCreation } from '../../context/interventionCreationContext';
import AssignmentTable from './AssignmentTable';
import TransitionReportBanner from './TransitionReportBanner';
import { assignedCount } from './assignmentRows';
import { AssignmentTransitionState } from './useAssignmentTransition';
import { useRequestsToAssign } from './useRequestsToAssign';
import TableFilters, { FilterSelect } from '../../../../../Tools/Table/TableFilters';
import TableSearch from '../../../../../Tools/Table/TableSearch';
import {
  FilterValue,
  NO_FILTERS,
  TableFilters as Filters,
  clearFilters,
  withSearch
} from '../../../../../Tools/Table/filters';
import {
  ASSIGN_FILTER_ENDPOINTS,
  useFilterFields,
  useStateFilterField
} from '../../useFilterFields';
import useDebouncedValue from '../../../../../../hooks/useDebouncedValue';

type ToAssignPanelProps = {
  transitionState: AssignmentTransitionState;
};

export default function ToAssignPanel({ transitionState }: ToAssignPanelProps) {
  const { t } = useTranslation();
  const { state } = useInterventionCreation();
  const { confirmDialog, closeDialog, dialogOptions } = useDialog();
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [areFiltersOpen, setAreFiltersOpen] = useState(false);
  const [search, setSearch] = useState('');

  // La frappe reste immédiate à l'écran ; seule la requête attend la fin de la saisie.
  const debouncedSearch = useDebouncedValue(search, 300);

  // Le type d'intervention n'existe que sur cette étape, comme sur la maquette.
  const filterFields = useFilterFields(ASSIGN_FILTER_ENDPOINTS, filters, {
    withInterventionType: true
  });
  const stateField = useStateFilterField('interventionCreation.filters.toAssign');

  const changeFilter = (name: string, value: FilterValue) =>
    setFilters((previous) => ({ ...previous, [name]: value }));

  // Le bouton ne vide que le bloc dépliable : l'état et la recherche, posés ailleurs
  // dans la barre d'outils, lui survivent.
  const resetFilters = () =>
    setFilters((previous) =>
      clearFilters(
        previous,
        filterFields.map((field) => field.name)
      )
    );

  const { rows, isFetching } = useRequestsToAssign(withSearch(filters, debouncedSearch));

  const selectedCount = state.selectedRequestIds.length;
  const readyToSend = assignedCount(rows);

  const askToSendBack = () => {
    const options: ConfirmDialogOptions = {
      title: t('interventionCreation.assignment.backToQualificationConfirmTitle'),
      content: t('interventionCreation.assignment.backToQualificationConfirmContent', {
        count: selectedCount
      }),
      onValidate: () => {
        // Fermer avant de lancer : deux modales superposées si la mutation échoue.
        closeDialog();
        transitionState.runTransition('sendBack');
      },
      onCancel: () => closeDialog()
    };

    confirmDialog(options);
  };

  return (
    <>
      <ConfirmDialog options={dialogOptions} />

      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="h6">{t('interventionCreation.assignment.listTitle')}</Typography>
              <Chip
                size="small"
                color="success"
                label={t('interventionCreation.assignment.readyToSend', { total: readyToSend })}
              />
            </Stack>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {/* La recherche prend la largeur restante, comme sur la maquette. */}
            <Box sx={{ flexGrow: 1, minWidth: 240 }}>
              <TableSearch value={search} onChange={setSearch} />
            </Box>
            <FilterSelect field={stateField} values={filters} onChange={changeFilter} />
            <Button
              variant="outlined"
              startIcon={<TuneIcon />}
              onClick={() => setAreFiltersOpen((open) => !open)}
            >
              {t('interventionCreation.filters.toggle')}
            </Button>
          </Box>

          {areFiltersOpen && (
            <TableFilters
              fields={filterFields}
              values={filters}
              onChange={changeFilter}
              onReset={resetFilters}
            />
          )}

          {transitionState.report !== null && (
            <TransitionReportBanner
              report={transitionState.report}
              successMessage={t(
                transitionState.report.transition === 'send'
                  ? 'interventionCreation.assignment.report.created'
                  : 'interventionCreation.assignment.report.sentBack',
                { count: transitionState.report.succeededCount }
              )}
              onDismiss={transitionState.dismiss}
            />
          )}

          {isFetching && <LinearProgress />}

          {/* Le renvoi descend dans le pied collé de la table : c'est le seul bandeau qui
              reste à l'écran pendant qu'on parcourt une liste de cent lignes, et c'est en
              la parcourant qu'on coche. Il est construit ici, où vivent sa confirmation et
              l'état partagé avec le panneau technicien. */}
          <AssignmentTable
            rows={rows}
            isLoading={isFetching}
            action={
              <Button
                variant="outlined"
                disabled={selectedCount === 0 || transitionState.isRunning}
                onClick={askToSendBack}
              >
                {t('interventionCreation.assignment.backToQualification', {
                  total: selectedCount
                })}
              </Button>
            }
          />
        </Stack>
      </Paper>
    </>
  );
}
